import { forwardRef, useEffect, useRef, useState } from 'react';
import { Button, Chevron, Filter, Input, Loader, type InputProps } from '..';
import { getLabel, useId } from '../utils';
import { Check, Close } from '@gjensidige/builders-icons';
import { useTracking } from '../utils';
import {
  createHiddenSelect,
  createVisualCheckbox,
  filterRef,
  getCombobox,
  getContentSize,
  getInput,
  getItems,
  onFilterClickToFocus,
  onKeyDown,
  useItems,
  type Item,
  type Items
} from './combobox-utils';
import styles from './combobox.module.css';
import stylesInput from '../input/input.module.css';
import clsx from 'clsx';
import '@u-elements/u-datalist';

export type ComboboxProps = Omit<
  InputProps,
  'defaultValue' | 'size' | 'value' | 'onChange'
> & {
  busy?: boolean;
  create?: boolean;
  defaultValues?: Item | Item[];
  filter?: boolean | 'custom';
  listProps?: React.ComponentPropsWithoutRef<'datalist'>;
  multiple?: boolean;
  onChange?: (values: Items, inputValue: string) => void;
  options?: Item | Item[];
  selectProps?: React.ComponentPropsWithoutRef<'select'>;
  size?: number | string | 'max-content';
  values?: Item | Item[];
};

let IS_UPDATED = false;
let HAS_FOCUS = false;

export const Combobox = forwardRef<HTMLInputElement, ComboboxProps>(
  function Combobox(
    {
      busy = false,
      create = false,
      defaultValues: uncontrolled,
      filter = true,
      list,
      listProps,
      multiple = false,
      name,
      onChange,
      onInput,
      options: optionsRaw,
      selectProps,
      size,
      suffix,
      tracking = true,
      values: controlled,
      wrapperProps,
      ...rest
    },
    ref
  ) {
    const [value, setValue] = useState('');
    const [values, setValues] = useState(useItems(controlled ?? uncontrolled));
    const listId = useId();
    const listRef = useRef<HTMLDataListElement>(null);
    const options = useItems(optionsRaw);
    const track = useTracking(tracking);

    if (!filter)
      console.warn(
        'Combobox: Using `filter={false}` is deprecated. Use `<Select>` in these cases instead.'
      );

    // Update if controlled values
    const prevControlled = useRef(controlled);
    if (controlled !== prevControlled.current) {
      prevControlled.current = controlled;
      setValues(getItems(controlled));
    }

    const update = (itemRaw: Item | false) => {
      const add = itemRaw ? getItems(itemRaw) : [];
      const remove = values.filter(({ value }) => value !== add[0]?.value);
      const isRemove = values.length !== remove.length;
      const next = multiple ? (isRemove ? remove : [...values, ...add]) : add;
      const select = listRef.current?.nextElementSibling;
      const value = getInput(listRef.current)?.value || '';

      IS_UPDATED = true;
      setValues(next);
      onChange?.(next, value);

      // Trigger hidden select change after React has updated the DOM
      window.requestAnimationFrame(() =>
        select?.dispatchEvent(new Event('change', { bubbles: true }))
      );
    };

    const handleInput = (event: React.KeyboardEvent<HTMLInputElement>) => {
      const input = event.currentTarget;
      const isOption = !(event.nativeEvent as unknown as InputEvent).inputType; // Detect if input is from keyboard or from clicking an option
      const query = input.value.toLowerCase().trim();
      let item = options.find(({ value }) => isOption && value === input.value);
      let text: string | null = null;

      // If clicking an non-filtered option, we want to get the value from the option and not from the input
      if (isOption && filter !== true) {
        const index = Number(input.value.match(/^\d+/));
        item = options[index];
        text = listRef.current?.options[index]?.text || item.value;
      }

      // If typing in a single mode, we want to automatically detect value matches
      if (!isOption && !multiple && !item) {
        if (create) {
          text = input.value;
          item = { label: text, value: text };
        } else if (values[0]) text = ''; // Will remove value if no match found
        Array.from(listRef.current?.options || [], (option, index) => {
          if (option.text.toLowerCase() === query) {
            item = options[index];
            text = option.text;
          }
        });
      }

      // If no item found, just do regular typing
      if (item || typeof text === 'string') {
        setValue((input.value = multiple ? '' : text || input.value)); // Update input value instantly before "onInput" and "onChange" runs
        update(item || false);

        // Use requestAnimationFrame to ensure it runs after the input value has been updated
        window.requestAnimationFrame(() => (input.scrollLeft = 0)); // Reset scroll position after selecting an option
      } else setValue(input.value);

      if (!isOption) onInput?.(event); // Prevent onInput if clicking an option
    };

    // We could have used event.relatedTarget, but Firefox incorrectly sets this as null too often
    const handleBlur = ({ currentTarget }: React.FocusEvent<HTMLElement>) => {
      setTimeout(() => {
        const focus = document.activeElement;
        const input = getInput(currentTarget);
        const self = currentTarget;

        if (input !== focus) setTimeout(() => (HAS_FOCUS = false), 300); // Let click event run first
        if (!self.contains(focus) && IS_UPDATED && !(IS_UPDATED = false))
          track({
            eventCategory: 'dropdown',
            eventAction: 'change',
            eventLabel: getLabel(input),
            eventStatus: values
              .map(({ value }) => value)
              .sort() // Sort to get consistent tracking logs
              .join('')
          });
      });
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>) =>
      onKeyDown(event, (removeIndex) => {
        if (typeof removeIndex === 'number') update(values[removeIndex]);
        else if (create && value) {
          event.preventDefault(); // Prevent typing if comma
          update(value);
          setValue(multiple ? '' : value);
        }
      });

    // Setup size and click-to-focus
    useEffect(() => {
      const self = getCombobox(listRef.current);
      const width = size === 'max-content' ? getContentSize(self) : `${size}ch`;
      const onClick = ({ target }: MouseEvent) =>
        HAS_FOCUS || target !== self || getInput(self)?.focus(); // Forward to focus to input if clicking on the combobox itself

      self?.addEventListener('click', onClick);
      self?.style.setProperty('--builders-combobox-size', size ? width : null);
      return () => self?.removeEventListener('click', onClick);
    }, [size]);

    // If single - fill the input
    useEffect(() => {
      if (multiple) return;
      const options = [...(listRef.current?.options || [])];
      const selected = options.find((opt) => opt.hasAttribute('data-selected'));
      if (values.length) return; // Avoid clearing value if create is true
      setValue(selected?.text ?? '');
    }, [multiple, values, create]);

    return (
      <Input
        {...rest}
        aria-busy={busy}
        list={list || listId}
        onFocus={(event) => (HAS_FOCUS = true) && rest.onFocus?.(event)}
        onInput={handleInput}
        readOnly={!filter}
        ref={ref}
        role="combobox"
        tracking={false} // We will do our own tracking
        value={value}
        wrapperProps={{
          ...wrapperProps, // Must be before className
          className: clsx(
            styles.combobox,
            !!multiple && styles.isMultiple,
            !!size && stylesInput.htmlSize,
            wrapperProps?.className
          ),
          onBlur: handleBlur,
          onKeyDown: handleKeyDown
        }}
        prefix={
          multiple &&
          values.map(({ label, value }, index) => (
            <Filter
              className={styles.filter}
              closeLabel="true" // Just need something to enable x-button
              closeProps={{
                'aria-hidden': true, // Hide x-button from keyboard and screen reader
                className: styles.close,
                tabIndex: -1
              }}
              key={`${index}-${value}`}
              onClick={onFilterClickToFocus}
              onClose={() => update(value)}
              ref={filterRef}
              role="button"
              tabIndex={-1} // Enable programatically moving focus to <Filter>
              tracking={false}
            >
              {label}
            </Filter>
          ))
        }
        suffix={
          <>
            {!multiple && (
              <Button
                aria-hidden="true"
                className={styles.clear}
                hidden={!value}
                icon={<Close />}
                tabIndex={-1}
                variant="icon"
                onClick={() => {
                  update(false);
                  setTimeout(() => getInput(listRef.current)?.focus(), 100); // Let React run update function before focusing input
                }}
              />
            )}
            {suffix || <Chevron className={styles.chevron} />}
            <u-datalist {...listProps} id={list || listId} ref={listRef}>
              {busy ? (
                <Loader variant="ellipsis" />
              ) : (
                options.map(({ label, value: val }, index) => {
                  const selected = values.some(({ value }) => val === value);

                  return (
                    <u-option
                      data-selected={selected || undefined}
                      key={`${index}-${val}`}
                      value={filter === true ? val : `${index}:${value}`} // Prevent <datalist> default filtering by setting input value as value off all <option>
                    >
                      {multiple
                        ? createVisualCheckbox(selected)
                        : selected && <Check />}
                      {label}
                    </u-option>
                  );
                })
              )}
            </u-datalist>
            {createHiddenSelect({ values, name, ...selectProps })}
          </>
        }
      />
    );
  }
);
